import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChatIcon from '@mui/icons-material/Chat';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LogoutIcon from '@mui/icons-material/Logout';
import { useFamily } from '../../context/FamilyContext';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { createCheckoutSession } from '../../services/api/familypal/config';

const MONTHLY_PRICE_ID = process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID || '';
const YEARLY_PRICE_ID = process.env.REACT_APP_STRIPE_YEARLY_PRICE_ID || '';

const features = [
  { icon: <SmartToyIcon />, title: 'AI Agent', description: 'Your intelligent family assistant' },
  { icon: <EmailIcon />, title: 'Family Email Address', description: 'Ingest mail from doctors, coaches & teachers' },
  { icon: <PeopleIcon />, title: 'Unlimited Family Members', description: 'Add everyone in your family' },
  { icon: <CalendarMonthIcon />, title: 'Shared Calendar', description: 'Keep everyone on the same page' },
  { icon: <ChatIcon />, title: 'Family Chat', description: 'Stay connected with messages' },
  { icon: <PhotoLibraryIcon />, title: 'Photo Albums', description: 'Share and preserve memories' },
  { icon: <LocalHospitalIcon />, title: 'Health Tracking', description: 'Medications, allergies & more' },
  { icon: <SchoolIcon />, title: 'School Management', description: 'Classes, assignments & grades' },
];

type Plan = 'monthly' | 'yearly';

const PaywallPage: React.FC = () => {
  const { currentFamilyId, allFamilies } = useFamily();
  const { familySubscriptions, refetch } = useSubscription();
  const { logout } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<Plan>('yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentFamilyName = allFamilies.find((f) => f.id === currentFamilyId)?.name || 'Your Family';

  const handleSubscribe = async () => {
    const priceId = selectedPlan === 'monthly' ? MONTHLY_PRICE_ID : YEARLY_PRICE_ID;
    if (!priceId) {
      setError('Stripe is not configured. Please contact support.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { checkout_url } = await createCheckoutSession({
        family_id: '',  // backend resolves from user's family membership
        price_id: priceId,
        success_url: `${window.location.origin}/?subscription=success`,
        cancel_url: `${window.location.origin}/paywall`,
      });
      window.location.href = checkout_url;
    } catch (err: any) {
      console.error('Checkout error:', err);
      const msg = err?.message || '';
      if (msg.includes('already has an active subscription')) {
        setError('This family already has an active subscription. Try refreshing the page.');
        // Refetch subscription status — it may have been created via mobile
        refetch();
      } else {
        setError('Failed to start checkout. Please try again.');
      }
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6 }}>
      <Container maxWidth="sm">
        {/* Logout */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            size="small"
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={logout}
            sx={{ color: 'text.secondary' }}
          >
            Log Out
          </Button>
        </Box>

        {/* Hero */}
        <Stack alignItems="center" spacing={1} sx={{ mb: 4 }}>
          <Box
            component="img"
            src="/logo192.png"
            alt="FamilyPal"
            sx={{ width: 80, height: 80, borderRadius: 2 }}
          />
          <Typography variant="h4" fontWeight="bold" textAlign="center">
            Subscribe for {currentFamilyName}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Everything your family needs to stay organized and connected
          </Typography>
        </Stack>

        {/* Family subscription statuses - shown when user has multiple families */}
        {familySubscriptions.length > 1 && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Your Families
              </Typography>
              <Stack spacing={1}>
                {familySubscriptions.map((fs) => (
                  <Stack key={fs.familyId} direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {fs.isActive ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : (
                        <CancelIcon fontSize="small" color="error" />
                      )}
                      <Typography variant="body2">
                        {fs.familyName}
                        {fs.familyId === currentFamilyId && (
                          <Typography component="span" variant="caption" color="text.secondary"> (current)</Typography>
                        )}
                      </Typography>
                    </Stack>
                    <Chip
                      label={fs.isActive ? 'Active' : 'Inactive'}
                      color={fs.isActive ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Trial banner */}
        <Alert
          severity="success"
          variant="outlined"
          sx={{ mb: 3, justifyContent: 'center' }}
        >
          Start with a 14-day free trial
        </Alert>

        {/* Plan selection */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          {/* Yearly */}
          <Card
            variant="outlined"
            onClick={() => setSelectedPlan('yearly')}
            sx={{
              flex: 1,
              cursor: 'pointer',
              borderColor: selectedPlan === 'yearly' ? 'primary.main' : 'divider',
              borderWidth: selectedPlan === 'yearly' ? 2 : 1,
              position: 'relative',
            }}
          >
            <CardContent>
              {selectedPlan === 'yearly' && (
                <Chip
                  label="SAVE 27%"
                  color="success"
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                />
              )}
              <Typography variant="subtitle2" fontWeight="bold">Annual</Typography>
              <Typography variant="h5" fontWeight="bold">$69.99</Typography>
              <Typography variant="caption" color="text.secondary">
                $5.83/month, billed annually
              </Typography>
            </CardContent>
          </Card>

          {/* Monthly */}
          <Card
            variant="outlined"
            onClick={() => setSelectedPlan('monthly')}
            sx={{
              flex: 1,
              cursor: 'pointer',
              borderColor: selectedPlan === 'monthly' ? 'primary.main' : 'divider',
              borderWidth: selectedPlan === 'monthly' ? 2 : 1,
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold">Monthly</Typography>
              <Typography variant="h5" fontWeight="bold">$7.99</Typography>
              <Typography variant="caption" color="text.secondary">
                Billed monthly
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        {/* Subscribe button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleSubscribe}
          disabled={isLoading}
          sx={{ py: 1.5, mb: 1, borderRadius: 2, textTransform: 'none', fontSize: '1rem' }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Start 14-Day Free Trial'}
        </Button>

        <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ mb: 4 }}>
          Then {selectedPlan === 'monthly' ? '$7.99/month' : '$69.99/year'}. Cancel anytime.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        )}

        {/* Features */}
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Everything included
        </Typography>
        <Stack spacing={2} sx={{ mb: 4 }}>
          {features.map((feature) => (
            <Stack key={feature.title} direction="row" spacing={2} alignItems="center">
              <Box sx={{ color: 'primary.main' }}>{feature.icon}</Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">{feature.title}</Typography>
                <Typography variant="caption" color="text.secondary">{feature.description}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>

        {/* Terms */}
        <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
          By subscribing, you agree to our terms of service. Your subscription will automatically
          renew unless canceled at least 24 hours before the end of the current period.
        </Typography>
      </Container>
    </Box>
  );
};

export default PaywallPage;
